import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import alertTriangleFill from '@iconify/icons-eva/alert-triangle-fill';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@material-ui/core';
import { useTheme, alpha } from '@material-ui/core/styles';

// ----------------------------------------------------------------------

const DEFAULT_MESSAGE =
  'No change in stock detected. To reflect the price change in the QB report, please update the quantity.';

const DEFAULT_CONTINUE_QUESTION = 'Do you want to continue without changing the qty?';

export default function InventoryUnitCostWarningDialog({
  open,
  onClose,
  onConfirm,
  title = 'Warning',
  message = DEFAULT_MESSAGE,
  continueQuestion = DEFAULT_CONTINUE_QUESTION,
  highlightText = 'QB report',
  cancelLabel = 'Cancel',
  confirmLabel = 'OK, Continue',
}) {
  const theme = useTheme();

  const highlightIndex = message.indexOf(highlightText);
  const messageBeforeHighlight = highlightIndex >= 0 ? message.slice(0, highlightIndex) : message;
  const messageAfterHighlight =
    highlightIndex >= 0 ? message.slice(highlightIndex + highlightText.length) : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="inventory-unit-cost-warning-title"
      aria-describedby="inventory-unit-cost-warning-description"
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: theme.palette.mode === 'dark' ? '#0f1b1e' : theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
          boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, 0.55)}, 0 0 40px ${alpha(theme.palette.warning.main, 0.12)}`,
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(6px)',
          bgcolor: alpha(theme.palette.common.black, 0.65),
        },
      }}
    >
      <Box
        sx={{
          height: 5,
          background: `linear-gradient(90deg, ${theme.palette.warning.dark}, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
        }}
      />
      <DialogContent
        id="inventory-unit-cost-warning-description"
        sx={{ pt: 4, pb: 2, px: 3.5, textAlign: 'center' }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.warning.main, 0.14),
              border: `2px solid ${alpha(theme.palette.warning.main, 0.45)}`,
              boxShadow: `0 0 36px ${alpha(theme.palette.warning.main, 0.35)}, inset 0 0 24px ${alpha(theme.palette.warning.main, 0.08)}`,
              animation: 'inventoryWarningPulse 2.4s ease-in-out infinite',
              '@keyframes inventoryWarningPulse': {
                '0%, 100%': {
                  boxShadow: `0 0 28px ${alpha(theme.palette.warning.main, 0.28)}, inset 0 0 20px ${alpha(theme.palette.warning.main, 0.06)}`,
                },
                '50%': {
                  boxShadow: `0 0 44px ${alpha(theme.palette.warning.main, 0.5)}, inset 0 0 28px ${alpha(theme.palette.warning.main, 0.12)}`,
                },
              },
            }}
          >
            <Icon
              icon={alertTriangleFill}
              width={44}
              height={44}
              style={{ color: theme.palette.warning.main }}
            />
          </Box>
        </Box>
        <Typography
          id="inventory-unit-cost-warning-title"
          variant="h5"
          sx={{ fontWeight: 700, mb: 1.5, letterSpacing: 0.2 }}
        >
          {title}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.75,
            maxWidth: 360,
            mx: 'auto',
          }}
        >
          {highlightIndex >= 0 ? (
            <>
              {messageBeforeHighlight}
              <Box component="span" sx={{ color: 'warning.main', fontWeight: 600 }}>
                {highlightText}
              </Box>
              {messageAfterHighlight}
            </>
          ) : (
            message
          )}
        </Typography>
        {continueQuestion ? (
          <Typography
            variant="subtitle1"
            sx={{
              mt: 2.5,
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.6,
              maxWidth: 360,
              mx: 'auto',
            }}
          >
            {continueQuestion}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'center',
          gap: 1.5,
          pb: 3.5,
          pt: 0.5,
          px: 3,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            minWidth: 110,
            borderRadius: 2,
            borderColor: alpha(theme.palette.grey[500], 0.4),
            color: 'text.secondary',
            '&:hover': {
              borderColor: alpha(theme.palette.grey[500], 0.7),
              bgcolor: alpha(theme.palette.grey[500], 0.08),
            },
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          sx={{
            minWidth: 130,
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
            background: 'linear-gradient(135deg, #0daad9 0%, #0891b2 100%)',
            boxShadow: `0 8px 24px ${alpha('#0daad9', 0.45)}`,
            '&:hover': {
              background: 'linear-gradient(135deg, #12bce8 0%, #0daad9 100%)',
              boxShadow: `0 12px 28px ${alpha('#0daad9', 0.55)}`,
            },
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

InventoryUnitCostWarningDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  continueQuestion: PropTypes.string,
  highlightText: PropTypes.string,
  cancelLabel: PropTypes.string,
  confirmLabel: PropTypes.string,
};
